import {
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FieldArray,
    FieldArrayRenderProps,
    useField,
    useFormikContext,
} from 'formik';
import { withStyles } from '@mui/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Button,
    CircularProgress,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormHelperText,
    TextField,
    Radio,
    ClickAwayListener,
    RadioGroup,
    Grow,
    ButtonProps,
    ButtonGroup,
    ButtonGroupProps,
    Autocomplete,
} from '@mui/material';
import DropdownComponents from './Dropdown';
import { basicClass } from 'assets/styles/modules/AppCompnents';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { DatePicker } from '@mui/x-date-pickers';
import { useMeasure, useTimeoutFn } from 'react-use';
import mergeRefs from 'merge-refs';

const AppThemeButton = withStyles(() => ({
    root: {
        '&:hover': {},
        '&.Mui-disabled': {
            opacity: '0.7',
        },
    },
}))(Button);

interface ButtonGroupInterface extends ButtonGroupProps {
    buttons: Omit<ButtonProps, 'color'>[];
    label: string;
    multichoice?: boolean;
    outerLabel: boolean;
    name?: string;
}
export interface ButtonInterface {
    className?: string;
    title: any;
    disabled?: boolean;
    fullWidth?: boolean;
    isLoading?: boolean;
    loadingSize?: number;
    loadingText?: string;
    testid?: string;
    loadingColor?:
        | 'primary'
        | 'secondary'
        | 'error'
        | 'info'
        | 'success'
        | 'warning'
        | 'inherit';
    endIcon?: any;
    startIcon?: any;
    onClick?: any;
    onMouseDown?: any;
    onBlur?: any;
    onFocus?: any;
    variant?: 'text' | 'outlined' | 'contained';
    color?: 'primary' | 'secondary';
    sx?: any;
    type?: any;
    innerRef?: React.MutableRefObject<HTMLButtonElement | null>;
    id?: string;
    role?: string;
    'aria-controls'?: string;
    'aria-haspopup'?:
        | boolean
        | 'grid'
        | 'listbox'
        | 'menu'
        | 'false'
        | 'true'
        | 'dialog'
        | 'tree';
    disableRipple?: boolean;
}

export const AppButton = ({
    className,
    title,
    disabled,
    fullWidth,
    isLoading,
    loadingText,
    loadingSize,
    loadingColor,
    endIcon,
    startIcon,
    onClick,
    onBlur,
    onFocus,
    variant,
    color,
    sx,
    testid,
    type,
    innerRef,
    onMouseDown,
    id,
    role,
    disableRipple,
    'aria-controls': aria,
    'aria-haspopup': ariapopup,
}: ButtonInterface): ReactElement => {
    const classNames = ['trans-3d-hover'];
    if (className) {
        classNames.push(className);
    }
    if (isLoading) {
        classNames.push('isLoading');
    }
    return (
        <AppThemeButton
            id={id}
            aria-controls={aria}
            aria-haspopup={ariapopup}
            role={role}
            sx={sx}
            onMouseDown={onMouseDown}
            ref={innerRef}
            disableRipple={disableRipple}
            data-testid={testid}
            type={type || 'submit'}
            className={classNames.join(' ')}
            disabled={disabled}
            fullWidth={fullWidth !== undefined ? fullWidth : false}
            startIcon={startIcon}
            endIcon={endIcon}
            onClick={onClick}
            onBlur={onBlur}
            onFocus={onFocus}
            variant={variant || 'contained'}
            color={color || 'primary'}
        >
            {isLoading && (
                <>
                    <CircularProgress
                        className="mr-2"
                        size={loadingSize}
                        color={loadingColor}
                        title="progress loading"
                    />
                    {(loadingText === '' || loadingText) && (
                        <span>{loadingText}</span>
                    )}
                    {loadingText !== '' && !loadingText && (
                        <span>PROCESSING</span>
                    )}
                </>
            )}
            {!isLoading && <Box>{title}</Box>}
        </AppThemeButton>
    );
};

export const AppPrimaryButton = (props: ButtonInterface): ReactElement => {
    const { className, innerRef } = props;
    return (
        <AppButton
            {...props}
            innerRef={innerRef}
            className={className ? `btn-primary ${className}` : `btn-primary`}
        />
    );
};

export const AppSecondaryButton = (props: ButtonInterface): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={
                className ? `btn-secondary ${className}` : `btn-secondary`
            }
        />
    );
};

export const AppPrimaryButtonOutlined = (
    props: ButtonInterface
): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={
                className
                    ? `btn-primary-outlined ${className}`
                    : `btn-primary-outlined`
            }
        />
    );
};

export const AppSecondaryButtonOutlined = (
    props: ButtonInterface
): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={
                className
                    ? `btn-secondary-outlined ${className}`
                    : `btn-secondary-outlined`
            }
        />
    );
};

export const AppTeritaryButton = (props: ButtonInterface): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={className ? `btn-tertiary ${className}` : `btn-tertiary`}
        />
    );
};

export const AppDarkButton = (props: ButtonInterface): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={className ? `btn-dark ${className}` : `btn-dark`}
        />
    );
};

export const AppPlainButton = (props: ButtonInterface): ReactElement => {
    const { className } = props;
    return (
        <AppButton
            {...props}
            className={className ? `btn-plain ${className}` : `btn-plain`}
        />
    );
};

/**
 * Button component.
 *
 * @param {Object} props - Component props.
 * @param {ReactNode} props.children - The content of the button.
 * @param {Object} props.sx - Additional styles for the button (optional).
 * @param {string} props.width - The width of the button (optional).
 * @param {string} [props.type] - The type of the button (mandatory).
 * @param {...any} [props.dynamicProps] - Additional dynamic props.
 * @returns {ReactElement} - Button component.
 */
export const ButtonComp = ({
    children,
    sx,
    width,
    type,
    ...dynamicProps
}: any): ReactElement => {
    const backgroundAction = (ev: string): string[] => {
        switch (ev) {
            case 'primary':
                return ['#1E56DA', 'white', ''];
            case 'secondary':
                return ['#01224B', 'white', ''];
            case 'outline':
                return ['white', '#1E56DA', '2px solid #1E56DA'];
            case 'neutral':
                return ['#E7E7E7', '#6B6B6B', ''];
            case 'warning':
                return ['#d00b28', 'white', ''];
            case 'success':
                return ['#2e7d32', 'white', ''];
            default:
                return ['', '', ''];
        }
    };

    const [bg, color, border] = backgroundAction(type);

    const buttonSx = !sx
        ? basicClass(width, bg, color, border)
        : { ...basicClass(width, bg, color, border), ...sx };

    return <AppPlainButton sx={buttonSx} {...dynamicProps} title={children} />;
};
export const AppFormikTextField = (props: any): ReactElement => {
    const {
        className,
        label,
        outerLabel,
        size,
        type,
        inputProps,
        onChange,
        placeholder,
        sx,
        disabled,
        disabledError,
        manualError,
        onError,
        onValid,
        onEnter,
        autoFocus,
        autoComplete,
        onElementSizing,
        inputRef,
        onBlur,
        onFocus,
    } = props;
    const [field, meta] = useField(props);
    const formik = useFormikContext();
    const fieldHelper = formik.getFieldHelpers(field.name);
    const [isTouched, setTouched] = useState<boolean>(false);
    const [arrayKey, setArrayKey] = useState<string[]>([]);
    const textInputRef = useRef<HTMLInputElement | null>(null);
    const innerFormHelper = useRef<null | HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (isTouched && meta.touched === false) {
            fieldHelper.setTouched(true);
        }
    }, [fieldHelper, isTouched, meta.touched]);
    useLayoutEffect(() => {
        if (
            arrayKey.length > 0 &&
            (arrayKey.at(-1) === 'v' || arrayKey.at(-1) === 'V') &&
            arrayKey.at(-2) === 'Control'
        ) {
            if (typeof onEnter === 'function') {
                fieldHelper.setValue(meta.value);
            }
        }
    }, [arrayKey.length]);

    const reinputProps = useMemo(
        () =>
            typeof inputProps === 'object' &&
            typeof inputProps.inputProps !== 'undefined'
                ? {
                      ...inputProps,
                      inputProps: {
                          ...inputProps.inputProps,
                          'aria-labelledby': outerLabel
                              ? 'textfield-typography'
                              : '',
                      },
                  }
                : {
                      ...inputProps,
                      inputProps: {
                          'aria-labelledby': outerLabel
                              ? 'textfield-typography'
                              : '',
                      },
                  },
        [inputProps, outerLabel]
    );
    useLayoutEffect(() => {
        if (disabledError || manualError) {
            if (typeof onError === 'function' && meta.error && meta.touched) {
                onError();
            }
            if (typeof onValid === 'function' && !meta.error) {
                onValid();
            }
        }
    }, [
        disabledError,
        manualError,
        meta.error,
        meta.touched,
        onError,
        onValid,
    ]);
    useLayoutEffect(() => {
        const layout = innerFormHelper.current?.getBoundingClientRect();

        if (typeof onElementSizing === 'function') {
            onElementSizing({ height: layout?.height, width: layout?.width });
        }
    }, [meta.error, meta.touched]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="textfield-typography"
                >
                    {label}
                </Typography>
            )}

            <TextField
                className={outerLabel ? `outer-label ${className}` : className}
                sx={sx}
                error={Boolean(meta.touched && meta.error)}
                fullWidth
                helperText={
                    (!disabledError || manualError) &&
                    meta.touched &&
                    meta.error
                }
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                margin="normal"
                name={field.name}
                label={outerLabel ? '' : label}
                placeholder={placeholder}
                type={type}
                value={meta.value}
                variant="outlined"
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (meta.touched === false) {
                            fieldHelper.setTouched(true);
                        }
                        if (typeof onEnter === 'function') {
                            onEnter(event);
                        }
                    } else if (event.key !== 'Control') {
                        if (meta.touched === false) {
                            setTouched(true);
                        }
                    }
                    if (typeof onEnter === 'function') {
                        setArrayKey((prev) => {
                            prev.push(event.key);
                            return prev;
                        });
                    }
                }}
                classes={{
                    root: 'container-text-field',
                }}
                onBlur={(e) => {
                    field.onBlur(e);
                    if (typeof onBlur === 'function') {
                        onBlur(e);
                    }
                }}
                onFocus={(e) => {
                    if (typeof onFocus === 'function') {
                        onFocus(e);
                    }
                }}
                onChange={(event: any) => {
                    field.onChange(event);

                    if (onChange) {
                        onChange(event);
                    }
                }}
                size={size || 'medium'}
                // aria-labelledby={outerLabel ? 'textfield-typography' : ''}
                InputProps={{
                    ...reinputProps,
                    classes: {
                        root: 'input-container',
                    },
                }}
                ref={innerFormHelper}
                inputRef={mergeRefs(textInputRef, inputRef)}
                disabled={disabled}
            />
        </>
    );
};

export const AppFormikTextFieldNumber = (props: any): ReactElement => {
    const {
        className,
        label,
        outerLabel,
        size,
        type,
        inputProps,
        onChange,
        onEnter,
        placeholder,
        sx,
        positiveLookAhead,
        autoFocus,
        autoComplete,
        onElementSizing,
        onBlur,
        onFocus,
    } = props;
    const [field, meta] = useField(props);
    // const [value, setValue] = useState<string>("")
    const formik = useFormikContext();
    const fieldHelper = formik.getFieldHelpers(field.name);
    const innerFormHelper = useRef<null | HTMLDivElement>(null);
    const reinputProps = useMemo(
        () =>
            typeof inputProps === 'object' &&
            typeof inputProps.inputProps !== 'undefined'
                ? {
                      ...inputProps,
                      inputProps: {
                          ...inputProps.inputProps,
                          'aria-labelledby': outerLabel
                              ? 'textfield-typography'
                              : '',
                      },
                  }
                : {
                      ...inputProps,
                      inputProps: {
                          'aria-labelledby': outerLabel
                              ? 'textfield-typography'
                              : '',
                      },
                  },
        [inputProps, outerLabel]
    );

    useLayoutEffect(() => {
        const layout = innerFormHelper.current?.getBoundingClientRect();

        if (typeof onElementSizing === 'function') {
            onElementSizing({ height: layout?.height, width: layout?.width });
        }
    }, [meta.error, meta.touched]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="number-textfield-typography"
                >
                    {label}
                </Typography>
            )}

            <TextField
                className={outerLabel ? `outer-label ${className}` : className}
                sx={sx}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                error={Boolean(meta.touched && meta.error)}
                fullWidth
                helperText={meta.touched && meta.error}
                margin="normal"
                name={field.name}
                label={outerLabel ? '' : label}
                placeholder={placeholder}
                type={type}
                value={Number.isNaN(meta.value) ? '' : meta.value}
                variant="outlined"
                onBlur={(event: any) => {
                    field.onBlur(event);
                    if (typeof onBlur === 'function') {
                        onBlur(event);
                    }
                }}
                onFocus={(e) => {
                    if (typeof onFocus === 'function') {
                        onFocus(e);
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (meta.touched === false) {
                            fieldHelper.setTouched(true);
                        }
                        if (typeof onEnter === 'function') {
                            onEnter(event);
                        }
                    }
                }}
                onChange={(event: any) => {
                    field.onChange(event);

                    if (/^\d+$/.test(event.target.value ?? '') === false) {
                        fieldHelper.setError('Only Numbers Allowed');
                        fieldHelper.setTouched(true, false);
                    }
                    const value = event.target.value.replace(/\D/, '');
                    fieldHelper.setValue(
                        typeof positiveLookAhead === 'boolean' &&
                            positiveLookAhead
                            ? value.replace(/^0/, '')
                            : value,
                        false
                    );

                    if (onChange) {
                        onChange(event);
                    }
                }}
                size={size || 'medium'}
                ref={innerFormHelper}
                InputProps={{
                    ...reinputProps,

                    classes: {
                        root: 'input-container',
                    },
                }}
            />
        </>
    );
};

export const AppFormikPasswordField = (props: any): ReactElement => {
    const {
        label,
        size,
        outerLabel,
        placeholder,
        className,
        onEnter,
        isCommonCopy,
        'aria-labelledby': aria,
    } = props;
    const [field, meta] = useField(props);
    const [showPassword, setShowPassword] = useState(false);
    const [arrayKey, setArrayKey] = useState<string[]>([]);

    const formik = useFormikContext();
    const fieldHelper = formik.getFieldHelpers(field.name);
    const [isTouched, setTouched] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (typeof isCommonCopy === 'boolean' && isCommonCopy) {
            if (isTouched && meta.touched === false) {
                fieldHelper.setTouched(true);
            }
        }
    }, [fieldHelper, isTouched, meta.touched]);
    useLayoutEffect(() => {
        if (typeof isCommonCopy === 'boolean' && isCommonCopy) {
            if (
                arrayKey.length > 0 &&
                (arrayKey.at(-1) === 'v' || arrayKey.at(-1) === 'V') &&
                arrayKey.at(-2) === 'Control'
            ) {
                if (typeof onEnter === 'function') {
                    fieldHelper.setValue(meta.value);
                }
            }
        }
    }, [arrayKey.length]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="password-typography"
                >
                    {label}
                </Typography>
            )}
            <TextField
                className={
                    outerLabel ? `outer-label ${className}` : `${className}`
                }
                error={Boolean(meta.touched && meta.error)}
                fullWidth
                helperText={meta.touched && meta.error}
                margin="normal"
                name={field.name}
                label={outerLabel ? '' : label}
                placeholder={placeholder}
                type={showPassword ? 'text' : 'password'}
                value={meta.value}
                variant="outlined"
                onBlur={field.onBlur}
                onChange={field.onChange}
                size={size || 'medium'}
                onKeyDown={(event) => {
                    if (typeof isCommonCopy === 'boolean' && isCommonCopy) {
                        if (event.key === 'Enter') {
                            if (meta.touched === false) {
                                fieldHelper.setTouched(true);
                            }
                            if (typeof onEnter === 'function') {
                                onEnter(event);
                            }
                        } else if (event.key !== 'Control') {
                            if (meta.touched === false) {
                                setTouched(true);
                            }
                        }
                        if (typeof onEnter === 'function') {
                            setArrayKey((prev) => {
                                prev.push(event.key);
                                return prev;
                            });
                        }
                    }
                }}
                // aria-labelledby={outerLabel ? 'password-typography' : ''}
                InputProps={{
                    inputProps: {
                        'aria-labelledby': outerLabel
                            ? 'password-typography'
                            : aria,
                    },
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => {
                                    setShowPassword(!showPassword);
                                }}
                                edge="end"
                                className="text-primary"
                                aria-label="Toggle show / hide password"
                            >
                                {showPassword ? (
                                    <VisibilityIcon />
                                ) : (
                                    <VisibilityOffIcon />
                                )}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </>
    );
};

export const AppFormikTextArea = (props: any): ReactElement => {
    const {
        label,
        outerLabel,
        readOnly,
        rows,
        notched,
        arialabelledby,
        ariadescribedby,
        placeholder,
        focused,
        sx,
        disabled,
        testid,
        endAdornment,
        maxLength,
        className,
        onChange,
    } = props;
    const [field, meta] = useField(props);
    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="text-secondary lato-regular"
                    id="textarea-typography"
                >
                    {label}
                </Typography>
            )}
            <TextField
                className={
                    outerLabel ? `outer-label ${className}` : `${className}`
                }
                aria-labelledby={
                    outerLabel && !arialabelledby ? 'textarea-typography' : ''
                }
                multiline
                rows={rows}
                error={Boolean(meta.touched && meta.error)}
                fullWidth
                helperText={meta.touched && meta.error}
                margin="normal"
                name={field.name}
                label={outerLabel ? '' : label}
                placeholder={placeholder || label}
                value={meta.value}
                sx={sx}
                disabled={disabled}
                variant="outlined"
                onBlur={field.onBlur}
                onChange={(e: any) => {
                    field.onChange(e);
                    if (onChange) onChange(e);
                }}
                classes={{
                    root: 'container-text-area',
                }}
                focused={focused}
                inputProps={{ 'data-testid': testid, maxLength }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                    readOnly,
                    notched,
                    classes: {
                        root: 'input-container',
                    },
                    'aria-labelledby': arialabelledby,
                    id: arialabelledby,
                    'aria-describedby': ariadescribedby,
                    endAdornment,
                }}
            />
        </>
    );
};

export const AppFormikButtonTiles = (
    props: ButtonGroupInterface
): ReactElement => {
    const { label, outerLabel, buttons, name, multichoice, ...innerProps } =
        props;
    const field = useMemo(() => name || 'tiles', [name]);

    const { handleBlur, getFieldMeta, submitCount } = useFormikContext();

    const error = useMemo(
        () => getFieldMeta(field).error,
        [getFieldMeta, field]
    );
    const [isButtonTouched, setButtonTouched] = useState<boolean>(false);
    const [items, setItems] = useState<Omit<ButtonProps, 'color'>[]>();
    useLayoutEffect(() => {
        setItems(buttons);
    }, [buttons]);

    const buttonGroupOnClick = useCallback(
        (arrayHelper: FieldArrayRenderProps, e: any, index: number) => {
            if (typeof multichoice === 'boolean' && multichoice) {
                const arrayValues = arrayHelper.form.values[field] as boolean[];
                const currentValues = arrayValues.at(index);
                arrayHelper.replace(index, !currentValues);
            } else {
                const arrayValues = arrayHelper.form.values[field] as boolean[];
                const currentValues = arrayValues.findIndex(
                    (value) => value === true
                );
                if (currentValues > -1) {
                    if (currentValues === index) {
                        arrayHelper.replace(index, false);
                    } else {
                        arrayHelper.swap(index, currentValues);
                    }
                } else {
                    arrayHelper.replace(index, true);
                }
            }
            setButtonTouched(true);
        },
        [field, multichoice]
    );
    const generateClassNameForButton = useCallback(
        (
            helpers: FieldArrayRenderProps,
            index: number,
            className: string | undefined
        ) => {
            const values = helpers.form.values[field] as boolean[];
            return values.at(index)
                ? `selected ${className}`
                : `not-selected ${className}`;
        },
        [field]
    );
    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="text-secondary lato-regular"
                    id="button-tiles-typography"
                >
                    {label}
                </Typography>
            )}
            <ButtonGroup
                {...innerProps}
                // aria-label={label}
                aria-labelledby={outerLabel ? 'button-tiles-typography' : ''}
            >
                <FieldArray
                    name={name ?? 'tiles'}
                    render={(arrayHelpers) =>
                        items &&
                        items.map((el, index) => (
                            <AppPrimaryButton
                                id={
                                    name
                                        ? `${name}[${index}]`
                                        : `tiles[${index}]`
                                }
                                title={el.value}
                                key={index}
                                {...el}
                                className={generateClassNameForButton(
                                    arrayHelpers,
                                    index,
                                    el.className
                                )}
                                onClick={(e: any) => {
                                    buttonGroupOnClick(arrayHelpers, e, index);
                                    if (typeof el.onClick === 'function') {
                                        el.onClick(e);
                                    }
                                }}
                                onBlur={(e: any) => {
                                    handleBlur(e);
                                }}
                            />
                        ))
                    }
                />
            </ButtonGroup>
            {error && (isButtonTouched || submitCount > 0) && (
                <FormHelperText error>{error}</FormHelperText>
            )}
        </>
    );
};

export const AppFormikDatePicker = (props: any): ReactElement => {
    const {
        className,
        label,
        outerLabel,
        size,
        type,
        inputProps,
        onChange,
        onChangeDate,
        onClose,
        onAccepted,
        placeholder,
        sx,
        disablePast,
        disableToday,
        onEnter,
        testid,
        autoFocus,
        autoComplete,
        disabled,
    } = props;
    const [field, meta] = useField(props);
    const formik = useFormikContext();
    const fieldHelper = formik.getFieldHelpers(field.name);
    const [initialTouch, setInitialTouch] = useState<boolean>();
    const [minDate, setMinDate] = useState<Date>();
    const [isError, setDateError] = useState<boolean>(false);

    useLayoutEffect(() => {
        const today = new Date();
        if (disableToday) {
            today.setDate(today.getDate() + 1);
            setMinDate(today);
        } else {
            today.setDate(new Date().getDate());
            setMinDate(today);
        }
    }, [disableToday]);
    useLayoutEffect(() => {
        if (initialTouch && typeof meta.value === 'undefined') {
            fieldHelper.setValue(minDate);
        }
    }, [meta.value, initialTouch, fieldHelper]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="datepicker-typography"
                >
                    {label}
                </Typography>
            )}

            <DatePicker
                autoFocus={autoFocus}
                disablePast={disablePast}
                disableHighlightToday={
                    (disableToday && typeof minDate === 'object') === true
                }
                disabled={disabled}
                minDate={
                    disableToday && typeof minDate === 'object'
                        ? minDate.toISOString()
                        : undefined
                }
                aria-labelledby={outerLabel ? 'datepicker-typography' : ''}
                data-testid={testid}
                inputFormat="dd/MM/yyyy"
                InputAdornmentProps={{ sx: { marginLeft: 0 } }}
                InputProps={{
                    ...inputProps,
                    sx,
                    // defaultValue,
                    classes: {
                        root: 'datepicker-input-container',
                        input: 'datepicker-input',
                    },

                    error: Boolean(meta.touched && meta.error),
                    helperText: meta.touched && meta.error,
                    name: field.name,
                    label: outerLabel ? '' : label,
                    placeholder,
                    size: size || 'medium',
                    type,
                    value: null,
                    onFocus: () => {
                        setInitialTouch(true);
                    },
                    onChange: (event: any) => {
                        // fieldHelper.setValue(event.target.value, false);
                        if (onChange) {
                            onChange(event);
                        }
                    },
                    onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            if (meta.touched === false) {
                                fieldHelper.setTouched(true);
                            }
                            if (typeof onEnter === 'function') {
                                onEnter(event);
                            }
                        }
                    },
                }}
                defaultCalendarMonth={undefined}
                onOpen={() => setInitialTouch(true)}
                onChange={(value: string | null) => {
                    if (value !== 'Invalid Date') {
                        setInitialTouch(true);

                        fieldHelper.setValue(value, false);
                        if (typeof onChangeDate === 'function') {
                            onChangeDate();
                        }
                    }
                }}
                onAccept={(value: string | null) => {
                    fieldHelper.setTouched(false);
                    fieldHelper.setValue(value);
                    setDateError(false);
                    if (typeof onAccepted === 'function') {
                        onAccepted();
                    }
                }}
                onClose={() => {
                    if (typeof onClose === 'function') {
                        onClose();
                    }
                }}
                onError={(reason) => {
                    if (
                        (reason === 'disablePast' ||
                            reason === 'minDate' ||
                            reason === 'invalidDate') &&
                        disabled === false
                    ) {
                        setDateError(true);
                    } else {
                        setDateError(false);
                    }

                    if (initialTouch) fieldHelper.setTouched(true);
                }}
                // onClose={() => fieldHelper.setTouched(false)}
                value={initialTouch || meta.value ? meta.value : null}
                renderInput={(props) => (
                    <TextField {...props} autoComplete={autoComplete} />
                )}
                className={outerLabel ? `outer-label ${className}` : className}
            />
            {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
            )}
            {meta.touched &&
                (typeof meta.error === 'undefined' ||
                    (typeof meta.error === 'boolean' &&
                        meta.error === false)) &&
                isError && (
                    <FormHelperText error>Date is invalid</FormHelperText>
                )}
        </>
    );
};

export const AppCheckBox = (props: any): ReactElement => {
    const { label, checked, onChange } = props;
    return (
        <FormControlLabel
            label={label}
            control={
                <Checkbox
                    checked={checked}
                    onChange={onChange}
                    color="primary"
                />
            }
        />
    );
};

export const ConfirmDialog = (props: any): ReactElement => {
    const {
        title,
        subTitle,
        showConfirm,
        cancelTitle,
        successTitle,
        onSuccess,
        onCancel,
        onCloseDialog,
        hideNegative,
    } = props;

    // Actions
    const actionPositive = (): void => {
        onSuccess();
    };
    const actionNegative = (): void => {
        onCancel();
    };
    const onClose = (): void => {
        if (onCloseDialog) {
            onCloseDialog();
        }
    };

    // UI Elements
    return (
        <Dialog fullWidth maxWidth="sm" open={showConfirm} onClose={onClose}>
            <Box padding={2}>
                <DialogContent className="dialog-content-container ">
                    <Typography className="white" variant="h3">
                        {title}
                    </Typography>
                    <Typography className="white">{subTitle}</Typography>
                </DialogContent>
                <DialogActions>
                    {!hideNegative && (
                        <AppSecondaryButton
                            className="btn-small"
                            title={cancelTitle}
                            onClick={actionNegative}
                        />
                    )}
                    <AppPrimaryButton
                        className="btn-small"
                        title={successTitle}
                        onClick={actionPositive}
                    />
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export const AppFormikSelect = (props: any): ReactElement => {
    const {
        name,
        items,
        readOnly,
        onChange,
        outerLabel,
        label,
        placeholder,
        notched,
        MenuProps,
        onOpen,
        onClose,
        testid,
        className,
        multiple,
        disabled,
        disableSaving,
        notEmphasized,
        IconComponentProps,
    } = props;

    const [field, meta] = useField(props);
    const IconComponentMemo = useMemo(() => {
        if (disabled && typeof placeholder === 'string') return undefined;
        return IconComponentProps || ArrowDropDownIcon;
    }, [IconComponentProps, disabled, placeholder]);
    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-select-field-container"
                    id="selectable-typography"
                >
                    {label}
                </Typography>
            )}
            <Select
                aria-labelledby={outerLabel ? 'selectable-typography' : ''}
                displayEmpty
                disabled={disabled}
                onOpen={onOpen}
                onClose={onClose}
                multiple={multiple}
                notched={notched}
                className={outerLabel ? `outer-label ${className}` : className}
                error={Boolean(meta.touched && meta.error)}
                label={outerLabel ? undefined : label}
                placeholder={placeholder}
                fullWidth
                margin="none"
                variant="outlined"
                IconComponent={IconComponentMemo}
                renderValue={(selected) => {
                    if (multiple && Array.isArray(selected)) {
                        if (selected.length === 0) {
                            if (
                                typeof notEmphasized === 'boolean' &&
                                notEmphasized
                            ) {
                                return placeholder;
                            }
                            return <em>{placeholder}</em>;
                        }

                        return selected.join(',');
                    }
                    if (selected) {
                        if (selected.length === 0) {
                            if (
                                typeof notEmphasized === 'boolean' &&
                                notEmphasized
                            ) {
                                return placeholder;
                            }
                            return <em>{placeholder}</em>;
                        }

                        return selected;
                    }
                    if (typeof notEmphasized === 'boolean' && notEmphasized) {
                        return placeholder;
                    }
                    return <em>{placeholder}</em>;
                }}
                value={disableSaving ? '' : meta.value}
                name={field.name}
                classes={{
                    select: 'select-list',
                }}
                MenuProps={MenuProps}
                onBlur={(e: any) => {
                    field.onBlur(e);
                    if (onChange) onChange(e);
                }}
                onChange={(e: any) => {
                    field.onChange(e);
                    if (onChange) onChange(e);
                }}
                inputProps={{ readOnly, 'data-testid': testid }}
            >
                {placeholder && (
                    <MenuItem disabled key={`${name}_${placeholder}`} value="">
                        <Typography className="text-tertiary">
                            {placeholder}
                        </Typography>
                    </MenuItem>
                )}
                {items.map((item: any, index: number): any => (
                    <MenuItem
                        key={`${name}_${index}`}
                        value={item}
                        className="black"
                    >
                        {item}
                    </MenuItem>
                ))}
            </Select>
            {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
            )}
        </>
    );
};

export const AppSelect = (props: any): ReactElement => {
    const { placeholder, onChange, label, outerLabel, items, readOnly } = props;
    const [field, meta] = useField(props);
    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-select-field-container"
                >
                    {label}
                </Typography>
            )}

            <Select
                aria-labelledby={outerLabel ? 'selectable-typography' : ''}
                className={outerLabel ? 'outer-label' : ''}
                error={Boolean(meta.touched && meta.error)}
                label={outerLabel ? undefined : label}
                fullWidth
                value={meta.value}
                name={field.name}
                variant="outlined"
                onBlur={(e: any) => {
                    field.onBlur(e);
                    if (onChange) onChange(e);
                }}
                onChange={(e: any) => {
                    field.onChange(e);
                    if (onChange) onChange(e);
                }}
                inputProps={{ readOnly }}
            >
                {placeholder && (
                    <MenuItem
                        disabled
                        key={`${field.name}_${placeholder}`}
                        value=""
                    >
                        <Typography className="text-tertiary">
                            {placeholder}
                        </Typography>
                    </MenuItem>
                )}
                {items.map((item: any, index: number) => (
                    <MenuItem
                        key={`${field.name}_${index}`}
                        value={item}
                        className="black"
                    >
                        {item}
                    </MenuItem>
                ))}
            </Select>

            {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
            )}
        </>
    );
};

export const AppRadioBox = (props: any): ReactElement => {
    const {
        onChange,
        label,
        icon,
        checked,
        checkedIcon,
        disabled,
        value,
        index,
        defaultChecked,
        testid,
        labelPlacement,
        className,
    } = props;
    return (
        <FormControlLabel
            key={index}
            classes={{
                root: 'label-checkbox',
                label: 'label-inside-checkbox',
            }}
            control={
                <Checkbox
                    data-testid={testid}
                    defaultChecked={defaultChecked}
                    checked={checked}
                    icon={icon}
                    disabled={disabled}
                    className={className}
                    checkedIcon={checkedIcon}
                    disableRipple
                    color="primary"
                    inputProps={{
                        'aria-checked': checked,
                        'aria-labelledby': 'checkbox-typography',
                    }}
                />
            }
            value={value}
            componentsProps={{ typography: { id: 'checkbox-typography' } }}
            label={label}
            labelPlacement={labelPlacement}
            onChange={onChange}
        />
    );
};

export const AppOutlinedRadioInput = (props: any): ReactElement => {
    const { onChange, label, checked, disabled, value } = props;
    return (
        <FormControlLabel
            control={
                <Radio
                    className="radio-sidebar-filtered"
                    checked={checked}
                    disabled={disabled}
                    disableRipple
                    inputProps={{
                        'aria-checked': checked,
                        'aria-labelledby': 'radio-typography',
                    }}
                />
            }
            componentsProps={{ typography: { id: 'radio-typography' } }}
            value={value}
            label={label}
            onChange={onChange}
        />
    );
};

export const AppFormikTextSelection = (props: any): ReactElement => {
    const {
        label,
        outerLabel,
        type,
        onChange,
        onChangeSelection,
        onBlurSelection,
        placeholder,
        options,
        disabled,
        fullWidth,
        getOptionLabel,
        renderOption,
        filterOptions,
        sx,
    } = props;

    const formik = useFormikContext();
    const [field, meta] = useField(props);

    const { setValue } = formik.getFieldHelpers(field.name);
    const [inputSelection, setInputSelection] = useState<any>();
    const [isBlurSelection, setBlurSelection] = useState<boolean>(false);
    const handleInputSelection = useCallback(
        (event: any, newInput: any) => {
            if (typeof newInput === 'string') {
                setValue(newInput);
            } else if (
                newInput &&
                typeof newInput === 'object' &&
                Array.isArray(newInput) === false &&
                Object.keys(newInput).some((e) => e === field.name)
            ) {
                const fieldInput = newInput[field.name];
                setValue(fieldInput);
            }
            setInputSelection(newInput);
            if (onChangeSelection) {
                onChangeSelection(event, newInput);
            }
        },
        [field.name, onChangeSelection, setValue]
    );
    const handleRegularBlurFromClosing = useCallback(
        (event: any) => {
            if (event.target.value !== 0 && event.target.value !== '') {
                if (typeof inputSelection === 'string') {
                    if (event.target.value !== inputSelection) {
                        setBlurSelection(true);
                    }
                } else if (
                    inputSelection &&
                    typeof inputSelection === 'object' &&
                    Array.isArray(inputSelection) === false &&
                    Object.keys(inputSelection).some((e) => e === field.name)
                ) {
                    const fieldInput = inputSelection[field.name];
                    if (event.target.value !== fieldInput) {
                        setBlurSelection(true);
                    }
                }
            }
        },
        [field.name, inputSelection]
    );
    useLayoutEffect(() => {
        if (isBlurSelection) {
            if (typeof inputSelection === 'string') {
                setValue(inputSelection);
            } else if (
                inputSelection &&
                typeof inputSelection === 'object' &&
                Array.isArray(inputSelection) === false &&
                Object.keys(inputSelection).some((e) => e === field.name)
            ) {
                const fieldInput = inputSelection[field.name];
                setValue(fieldInput);
            }
            if (onBlurSelection) {
                onBlurSelection(inputSelection);
            }
            setBlurSelection(false);
        }
    }, [isBlurSelection]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="text-selection-typography"
                >
                    {label}
                </Typography>
            )}

            <Autocomplete
                options={options}
                sx={sx}
                getOptionDisabled={disabled}
                getOptionLabel={getOptionLabel}
                filterOptions={filterOptions}
                renderOption={renderOption}
                onChange={handleInputSelection}
                onInputChange={(event, value) => {
                    if (onChange) {
                        onChange(event, value);
                    }
                }}
                onClose={handleRegularBlurFromClosing}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={Boolean(meta.touched && meta.error)}
                        fullWidth={fullWidth}
                        helperText={meta.touched && meta.error}
                        margin="normal"
                        name={field.name}
                        label={outerLabel ? '' : label}
                        placeholder={placeholder}
                        type={type}
                        value={meta.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        disabled={disabled}
                        aria-labelledby={
                            outerLabel ? 'text-selection-typography' : ''
                        }
                    />
                )}
            />
        </>
    );
};
export const AppFormikTextSelect = (props: any): ReactElement => {
    const {
        className,
        label,
        outerLabel,
        size,
        type,
        inputProps,
        onChange,
        placeholder,
        options,
        path,
        innerRef,
        isOpen,
        setIsOpen,
        autoComplete,
        isCaseSensitive,
        sx,
    } = props;

    const [field, meta] = useField(props);
    const [optionsData, setOptionsData] = useState<string[]>(options ?? []);
    const [value, setValue] = useState<string>('');
    const [innerTextRef, { width: widthText }] = useMeasure<HTMLInputElement>();
    const formik = useFormikContext();
    const innerWrapperContent = useRef<HTMLDivElement | null>(null);
    const fieldHelper = formik.getFieldHelpers(field.name);
    const fieldErrorHelper = formik.getFieldHelpers(`${field.name}_error`);
    const notSelectableErrorHelper = formik.getFieldHelpers(
        `${field.name}_notselectable`
    );
    const notSelectableMeta = formik.getFieldMeta(
        `${field.name}_notselectable`
    );
    const [isOptionsError, setOptionsIsError] = useState<boolean>(false);

    const [isError, setError] = useState<boolean>(false);

    useEffect(() => {
        if (
            meta.touched &&
            (!isOpen || optionsData.length === 0) &&
            meta.error
        ) {
            setError(true);
        } else {
            setError(false);
        }
    }, [isOpen, meta.error, meta.touched, optionsData.length]);

    useLayoutEffect(() => {
        fieldErrorHelper.setValue(isOptionsError);
    }, [isOptionsError]);
    useLayoutEffect(() => {
        if (optionsData.length === 0) {
            if (meta.touched === false) {
                fieldHelper.setTouched(true, true);
            }
            setOptionsIsError(true);
        } else {
            setOptionsIsError(false);
        }
    }, [optionsData.length]);

    useLayoutEffect(() => {
        if (!isOpen && value === '') {
            fieldHelper.setTouched(true, true);
        }
    }, [isOpen, value]);

    useEffect(() => {
        if (field.value !== '') {
            if (isCaseSensitive) {
                const optionsFilter: any[] = Array.from<any>(options).filter(
                    (el) => String(el[path]).includes(field.value) === true
                );
                if (isOpen !== false) setOptionsData(optionsFilter);
            } else {
                const optionsFilter: any[] = Array.from<any>(options).filter(
                    (el) =>
                        String(el[path])
                            .toLowerCase()
                            .includes(String(field.value).toLowerCase()) ===
                        true
                );
                if (isOpen !== false) setOptionsData(optionsFilter);
            }
        } else {
            setOptionsData(options);
        }
    }, [field.value, options, path]);
    const [arrowChanges, setArrowChanges] = useState<boolean>(false);
    const [num, setNum] = useState<number>(0);
    const handleFocusActionFromInput = useCallback(() => {
        if (isOpen) {
            const { current: wrapper } = innerWrapperContent;
            if (wrapper) {
                const node: HTMLDivElement | null = wrapper.children.item(
                    num
                ) as HTMLDivElement;
                if (node) {
                    node.focus();
                }
            }
        }
    }, [isOpen, num]);

    const [, cancel, reset] = useTimeoutFn(handleFocusActionFromInput, 100);
    useEffect(() => {
        cancel();
    }, []);
    const handleInputClick = useCallback(() => {
        cancel();
    }, [cancel]);
    const handleInputKeyDown = useCallback(
        (event: any) => {
            if (event.key === 'ArrowDown') {
                reset();
            }
        },
        [reset]
    );
    const handleInputFocus = useCallback(() => {
        setIsOpen(true);
        fieldHelper.setTouched(true, true);
    }, [setIsOpen]);

    useEffect(() => {
        if (arrowChanges) {
            reset();
            setArrowChanges(false);
        }
    }, [arrowChanges]);
    useEffect(() => {
        if (isOpen) {
            setNum(0);
            reset();
        }
    }, [isOpen]);

    const handleInputBlur = useCallback(() => {
        if (
            field.value !== '' &&
            value !== field.value &&
            notSelectableMeta.value !== true
        ) {
            // fieldHelper.setTouched(true, false);
            notSelectableErrorHelper.setValue(true, true);
        } else {
            if (optionsData.length !== 0 && value === field.value) {
                fieldHelper.setTouched(false, true);
            }

            if (field.value === '' || value === '') {
                notSelectableErrorHelper.setValue(false, true);
            }
        }

        return !isOpen ? field.onBlur : undefined;
    }, [
        field.onBlur,
        field.value,
        fieldHelper,
        isOpen,
        notSelectableErrorHelper,
        notSelectableMeta.value,
        optionsData.length,
        value,
    ]);

    return (
        <>
            {outerLabel && (
                <Typography
                    variant="body1"
                    className="formik-text-field-container"
                    id="text-select-typography"
                >
                    {label}
                </Typography>
            )}
            <TextField
                name={`${field.name}_error`}
                inputProps={{
                    type: 'hidden',
                }}
                sx={{ display: 'none' }}
            />
            <TextField
                name={`${field.name}_notselectable`}
                inputProps={{
                    type: 'hidden',
                }}
                sx={{ display: 'none' }}
            />
            <ClickAwayListener
                onClickAway={() => {
                    setIsOpen(false);
                }}
            >
                <Box
                    className={`wrapper-checklist ${
                        isOpen && optionsData.length > 0 ? 'open' : 'close'
                    }`}
                >
                    <TextField
                        className={
                            outerLabel ? `outer-label ${className}` : className
                        }
                        ref={innerTextRef}
                        sx={{
                            input: {
                                cursor: 'pointer',
                                color: `${
                                    !value || isOpen ? '#929292' : '#3A3A3A'
                                }`,
                                fontWeight: `${!value || isOpen ? '' : 'bold'}`,
                            },
                            background: `${!value || isOpen ? '#F6F6F6' : ''}`,
                            ...sx,
                        }}
                        aria-labelledby={
                            outerLabel ? 'text-select-typography' : ''
                        }
                        error={Boolean(isError)}
                        fullWidth
                        // helperText={meta.touched && !isOpen && meta.error}
                        margin="normal"
                        name={field.name}
                        label={outerLabel ? '' : label}
                        placeholder={placeholder}
                        type={type}
                        value={meta.value}
                        variant="outlined"
                        onBlur={handleInputBlur}
                        onFocus={handleInputFocus}
                        onClick={handleInputClick}
                        onKeyDown={handleInputKeyDown}
                        onChange={(e) => {
                            fieldHelper.setValue(e.target.value);
                        }}
                        size={size || 'medium'}
                        InputProps={{
                            ...inputProps,

                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle dropdown visibility"
                                        onClick={() => setIsOpen(!isOpen)}
                                        edge="end"
                                    >
                                        {isOpen ? (
                                            <ArrowDropUpIcon />
                                        ) : (
                                            <ArrowDropDownIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        autoComplete={autoComplete}
                    />
                    <Grow
                        in={isOpen && optionsData.length > 0}
                        style={{ transformOrigin: '0 0 0' }}
                        {...(isOpen ? { timeout: 200 } : {})}
                    >
                        <Box>
                            <DropdownComponents
                                width={widthText}
                                setValue={(e: any) => {
                                    setValue(e.name);
                                    notSelectableErrorHelper.setValue(
                                        false,
                                        false
                                    );
                                    fieldHelper.setValue(e.name);
                                    fieldHelper.setTouched(false, false);
                                    onChange(e);
                                    setIsOpen(false);
                                }}
                                options={optionsData}
                                path={path}
                                innerRef={mergeRefs(
                                    innerRef,
                                    innerWrapperContent
                                )}
                                onKeyArrowUp={() => {
                                    setNum((prev) => {
                                        if (prev > 0) {
                                            return prev - 1;
                                        }
                                        return optionsData.length - 1;
                                    });
                                    setArrowChanges(true);
                                }}
                                onKeyArrowDown={() => {
                                    setNum((prev) => {
                                        if (prev < optionsData.length - 1) {
                                            return prev + 1;
                                        }
                                        return 0;
                                    });
                                    setArrowChanges(true);
                                }}
                                onNumFocus={(index) => setNum(index)}
                            />
                        </Box>
                    </Grow>
                    {isError && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                    )}
                </Box>
            </ClickAwayListener>
        </>
    );
};

const renderOptions = (options: any): ReactElement =>
    options.map((option: any) => (
        <FormControlLabel
            key={option}
            value={option}
            control={<Radio />}
            label={option}
        />
    ));

export const AppFormikRadioGroup = (props: any): ReactElement => {
    const { options, children } = props;

    const [field, meta] = useField(props);

    return (
        <>
            <RadioGroup {...field} {...props} name={field.name}>
                {/* Here you either map over the props and render radios from them,
         or just render the children if you're using the function as a child */}
                {options ? renderOptions(options) : children}
            </RadioGroup>

            {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
            )}
        </>
    );
};

/**
 * Button component for AzureAD login with loading indicator.
 */
export const ButtonAAD = ({
    callback,
    isLoading,
    disabled,
    loadingText,
}: {
    callback: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    loadingText?: string;
}): ReactElement => (
    <AppPrimaryButton
        fullWidth
        title="Log In With Microsoft"
        isLoading={isLoading}
        loadingText={loadingText || 'PROCESSING'}
        loadingSize={22}
        onClick={() => callback()}
        startIcon={
            <img
                alt="Microsoft Single sign-on"
                src="/images/brand/Microsoft.png"
                className="microsoft-icon"
            />
        }
        disabled={isLoading || disabled}
    />
);
